import React, { ReactNode } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { BaseButton } from '../../../base'
import { ButtonVariant } from '../../../base/BaseButton/BaseButton'

const defaultSx = {
  backButton: { margin: '24px 0px' },
  continueButton: { margin: '0px 0px 24px' },
  circularProgress: { marginRight: '0px' },
}

export interface NavigationButtonsProps {
  children?: ReactNode
  isEnd?: boolean
  isStart?: boolean
  isSubmitting?: boolean
  isTop?: boolean
  labelBack?: string
  labelContinue?: string
  labelSubmit?: string
  onSubmit?: Function
  onClickBack?: Function
  onClickContinue?: Function
}

/**
 *
 * @param {NavigationButtonsProps} props
 * @param {boolean} props.isEnd (optional) - Shows only the submit button
 * @param {boolean} props.isStart (optional) - Shows only the continue button
 * @param {boolean} props.isSubmitting (optional) - When the data form is submitting
 * @param {boolean} props.isTop (optional) - Shows the submit button at the top
 * @param {string} props.labelBack (optional) - The label for the back button
 * @param {string} props.labelContinue (optional) - The label for the continue button
 * @param {string} props.labelSubmit (optional) - The label for the submit button
 * @param {Function} props.onSubmit (optional) - Callback to submit the data form
 * @param {Function} props.onClickBack (optional) - Callback to routing history back
 * @param {Function} props.onClickContinue (optional) - Callback to continue the flow
 */
const NavigationButtons = (props: NavigationButtonsProps) => {
  const shouldRenderBack = !props.isStart && !props.isTop
  const shouldRenderContinue = !props.isEnd && !props.isTop
  const shouldRenderSubmit = props.isEnd && !props.isTop
  const shouldRenderOnTop = props.isEnd && props.isTop
  const shouldRenderProgress = props?.isSubmitting && (
    <CircularProgress size={30} sx={defaultSx.circularProgress} />
  )

  const onClickBack = () => {
    props.onClickBack && props.onClickBack()
  }

  const onClickContinue = () => {
    props.onClickContinue && props.onClickContinue()
  }

  const onClickSubmit = () => {
    props.onSubmit && props.onSubmit()
  }

  return (
    <>
      {shouldRenderOnTop && (
        <BaseButton
          disabled={props?.isSubmitting}
          onClick={onClickSubmit}
          variant={ButtonVariant.CONTAINED}
        >
          {shouldRenderProgress}
          {!props?.isSubmitting && props.labelSubmit}
        </BaseButton>
      )}

      {shouldRenderContinue && (
        <BaseButton
          disabled={props.isSubmitting}
          onClick={onClickContinue}
          variant={ButtonVariant.CONTAINED}
          sx={shouldRenderBack ? {} : defaultSx.continueButton}
        >
          {shouldRenderProgress}
          {!props?.isSubmitting && props.labelContinue}
        </BaseButton>
      )}

      {shouldRenderSubmit && (
        <BaseButton
          disabled={props?.isSubmitting}
          onClick={onClickSubmit}
          variant={ButtonVariant.CONTAINED}
        >
          {shouldRenderProgress}
          {props.labelSubmit}
        </BaseButton>
      )}

      {shouldRenderBack && (
        <BaseButton
          disabled={props.isSubmitting}
          onClick={onClickBack}
          sx={defaultSx.backButton}
          variant={ButtonVariant.OUTLINED}
        >
          {props.labelBack}
        </BaseButton>
      )}
    </>
  )
}

NavigationButtons.defaultProps = {
  labelBack: 'Back',
  labelContinue: 'Continue',
  labelSubmit: 'Submit Payment',
  isSubmitting: false,
}

export default NavigationButtons
